import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { ProductStatusId } from "../../../js/models/maxEnums";
import { BasketPageCheckoutOrderModel, CheckoutOrderViewItem } from "./checkoutOrderView";
import { useModifyProductQuantityMutation } from "./basketHooks";
import currencyFormatter, { Currency } from "@/common/CurrencyFormatter";

interface BasketListItemProperties {
    readonly item: CheckoutOrderViewItem;
    readonly onModelChange: (model: BasketPageCheckoutOrderModel) => void;
    readonly currency: Currency;
}

export default function BasketListItem({ item, onModelChange, currency }: BasketListItemProperties): React.JSX.Element {
    const { t } = useTranslation("basket");
    const modifyQuantityMutation = useModifyProductQuantityMutation(onModelChange);

    const modifyQuantity = (productId: number, newQuantity: number): void => {
        modifyQuantityMutation.mutate({ productId: productId, newQuantity: newQuantity });
    };

    const availabilityBlock = (): React.JSX.Element => <>
        {item.availableQuantity > 1
          && <div className="cart-control">
              {item.quantity > 0
                && <div
                    className="less-cart"
                    onClick={(): void => modifyQuantity(item.productId, item.quantity - 1)}
                >
                    -
                </div>}
              <span>{item.quantity} {t("pieces")}</span>
              {item.quantity < item.availableQuantity
                && <div
                    onClick={(): void => modifyQuantity(item.productId, item.quantity + 1)}
                    className="add-cart"
                >
                    +
                </div>}
              {item.isMarketplace
                  ? <div className="kos--text">{t("piecesPartner")}</div>
                  : <div className="kos--text">{t("piecesInStock")}</div>}
          </div>}
        {item.availableQuantity === 0 && <div className="cart-item-sold">
            <span>{t("sold")}</span>
            {item.groupQuantity > 0 && <a href={item.url}>{t("selectAnotherVariant")}</a>}
        </div>}

        {item.availableQuantity === 1 && (item.isMarketplace
            ? <div className="kos--text">{t("lastPiecePartner")}</div>
            : <div className="kos-red-text">{t("lastPieceInStock")}</div>
        )}

        {item.inAnotherBasketQuantity > 0
          && <div className="cart-item-added-info">
              <div className="dash-only-desktop">- </div><div className="first-letter-large-mobile">{t("anotherCustomerInBasket", { count: item.inAnotherBasketQuantity })}</div>
          </div>}
    </>;

    return (
        <div className="cart-item-wrap product-item">
            <div className="cart-item-inner">
                <div className="cart-item-product-image">
                    <span className="cart-item-product-image-helper"></span>
                    {item.imageUrl !== null && <img src={item.imageUrl} alt={item.title} width="170" height="170" />}
                </div>
                <div className="cart-item-product-title-availability-labels">
                    <a href={item.url}>{item.title}</a>
                    <br />
                    <div className="cart-item-quality">{t("state")}:&nbsp;<span className={classNames(
                        item.productStatusId === ProductStatusId.NotWorking && "cart-item-quality-red",
                        item.productStatusId === ProductStatusId.Unknown && "cart-item-quality-grey",
                    )}>{item.productStatus}</span>
                    </div>
                    <div className="pid-grey"> | {t("variant")} {item.productId}</div><br />
                    {availabilityBlock()}
                </div>

                <div className="cart-item-product-price">
                    {item.retailPrice !== null
                      && <span className="price-was">
                          {currencyFormatter.formatCurrencyWithSymbol(item.retailPrice, currency)}
                      </span>}

                    {item.quantity > 1 && <span className="unit-price">{t("pricePerPiece")}</span>}
                    <span className={`price-now${item.price > item.priceWithDiscount
                        ? " reduced-price"
                        : ""}`}>
                        {currencyFormatter.formatCurrencyWithSymbol(item.price, currency)}
                    </span>
                    {item.price > item.priceWithDiscount
                      && <span className="price-with-discount-caption">
                          <span className="price-with-discount-value">
                              {currencyFormatter.formatCurrencyWithSymbol(item.priceWithDiscount, currency)}
                          </span>
                      </span>}
                </div>
                <div className="cart-item-product-remove">
                    <a
                        href="#"
                        onClick={(event): void => {
                            event.preventDefault();
                            modifyQuantity(item.productId, 0);
                        }}
                    >
                        <i className="fa-solid fa-trash-can"></i>
                    </a>
                </div>
            </div>
        </div>
    );
}
